'use client';

import useSWR, { type SWRResponse } from 'swr';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import {
  FetchStatusError,
  fetchWithTracingAndLogging,
  shouldRetryOnServerError,
} from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { adaptHotelStaticDataResponse, type HotelStaticData } from '../common/staticHotelDataAdapter.ts';
import { useTranslation } from '../../translations/Translation.tsx';
import { isValidHotelId } from '../common/common.helper.ts';
import { withPlaceholderData } from './withPlaceholderData.ts';

// A timed-out or rate-limited request clears on its own, so those two are worth another go; every
// other 4xx is the server saying this request will never succeed.
const RETRYABLE_CLIENT_ERROR_STATUSES = [408, 429];

function shouldRetryHotelStatic(error: unknown) {
  return (
    shouldRetryOnServerError(error) ||
    (error instanceof FetchStatusError && RETRYABLE_CLIENT_ERROR_STATUSES.includes(error.status))
  );
}

/**
 * Static hotel data (travel-api), served as the memsearch-derived placeholder from the search page
 * until the request resolves.
 *
 * The placeholder is deliberately NOT handed to SWR as `fallbackData`; it is merged back in
 * afterwards by `withPlaceholderData`, which is where the reason lives (MPH-964).
 */
export function useFetchHotelStatic(): SWRResponse<HotelStaticData | null> {
  const initialParams = useInitialParams();
  const { localization, searchInfoData, commonData } = usePageWrapperData();
  const { t } = useTranslation();
  const { staticHotelFallbackData } = initialParams;

  const swrResponse = useSWR<HotelStaticData | null>(
    isValidHotelId(initialParams.internalHotelId)
      ? `${BASE_PATH}/api/hotel-detail/static?id=${initialParams.internalHotelId}&businessProfileId=${localization.businessProfile}&searchMode=${searchInfoData.searchMode}`
      : null,
    async (url) =>
      fetchWithTracingAndLogging<StaticHotelDetail>(
        url,
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
          },
        },
        getLoggingInfo(initialParams.pageType, 'static', 'useFetchHotelStatic'),
        searchInfoData.searchTraceParent,
        commonData
      ).then((response) =>
        adaptHotelStaticDataResponse(response as HotelDetailSuccess, t, initialParams.staticHotelFallbackData?.images)
      ),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 1000 * 60 * 30,
      revalidateOnMount: true,
      shouldRetryOnError: shouldRetryHotelStatic,
      // SWR's retry is uncapped by default. Consumers read `isLoading` to tell "no answer yet"
      // from "answered with nothing", and every retry raises it again, so an unbounded loop would
      // flash their placeholders for as long as the endpoint stayed down.
      errorRetryCount: 2,
    }
  );

  return withPlaceholderData(swrResponse, staticHotelFallbackData);
}

// The loading flag the sections should render on. `isLoading` on its own goes back up for each
// retry; once an error is in hand the answer is not imminent, so the empty state is the honest
// thing to show while the remaining retries run in the background.
export function useIsHotelStaticLoading() {
  const { isLoading, error } = useFetchHotelStatic();

  return isLoading && !error;
}
